import { extendZodWithOpenApi } from "zod-openapi";
extendZodWithOpenApi(z);

import { ActionType } from "block-system/types";
import {
  BlockBaseSchema,
  BlockIdSchema,
  ComponentBlockStyleSchema,
} from "block-system/types/base";
import {
  NavigateActionSchema,
  NotificationActionSchema,
  OpenExternalUrlActionSchema,
} from "block-system/types/common";
import { z } from "zod";
import { FieldBlockSchema } from "../Field/schema";
import { blockType } from "./constants";
import { TableIdSchema } from "@/server/schemas/table-tables";

// Form actions
// i.e. Actions that are stored in the form block config in the page content
// -----------------------------------------------------------------------------

export const FormBlockActionSchema = z
  .discriminatedUnion("type", [
    NavigateActionSchema,
    NotificationActionSchema,
    OpenExternalUrlActionSchema,
  ])
  .openapi({ ref: "FormBlockAction" });

export type FormBlockAction = z.infer<typeof FormBlockActionSchema>;

export type FormBlockActionType = FormBlockAction["type"];

// Form block
// -----------------------------------------------------------------------------

export const StorageTypeSchema = z.enum(["tables"]);

export type StorageType = z.infer<typeof StorageTypeSchema>;

export const ConfirmationActionTypes: (ActionType | FormBlockActionType)[] = [
  "navigate",
  "notification",
  "openExternalUrl",
];

const FormBlockActions = z
  .array(FormBlockActionSchema)
  .min(1, { message: "Forms must have at least one action" })
  .refine(
    (actions) => {
      const confirmationActions = actions.filter((t) =>
        ConfirmationActionTypes.includes(t.type)
      );
      return (
        confirmationActions.length >= 1 &&
        !ConfirmationActionTypes.some(
          (item) => actions.filter((t) => t.type === item).length > 1
        )
      );
    },
    {
      message: "Forms must have one confirmation action",
    }
  );

export const FormBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: z.object({
    id: BlockIdSchema.optional(),
    submitText: z.string(),
    storageType: StorageTypeSchema.optional().describe(
      "The type of storage to use for form submissions. Currently, only 'tables' is supported."
    ),
    storageId: TableIdSchema.optional().describe(
      "The ID of the Zapier Table connected to this form."
    ),
    storageCreatedFromInterfaces: z.boolean().optional(),
    triggers: FormBlockActions.describe(`
      Actions to perform when the form is submitted. The actions should adhere to the following rules:
      1. Must have one confirmation action (navigate, notification, or openExternalUrl).
      2. Cannot have more than one confirmation action of the same type (e.g., two navigate actions).
      3. Cannot have both a navigate and openExternalUrl action.`),
    style: ComponentBlockStyleSchema.optional(),
    captchaEnabled: z.boolean().default(false),
  }),
  children: z.array(FieldBlockSchema),
}).openapi({ ref: "FormBlock" });

export type FormBlock = z.infer<typeof FormBlockSchema>;
